import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import axios from 'axios';

// Usar o mesmo domínio que a página está sendo servida
const API = typeof window !== 'undefined' ? `${window.location.origin}/api` : '/api';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const isCheckingAuth = useRef(false);

  const checkAuth = async () => {
    // Evitar chamadas simultâneas
    if (isCheckingAuth.current) return;
    isCheckingAuth.current = true;
    
    const token = localStorage.getItem('token');
    
    // Tentar autenticação via JWT token primeiro
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
        setLoading(false);
        isCheckingAuth.current = false;
        return;
      } catch (error) {
        // Só remover token se o servidor respondeu com 401/403 (token inválido)
        // NÃO remover em caso de erro de rede/timeout (pode ser temporário)
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          console.log('Token inválido, removendo...');
          localStorage.removeItem('token');
        } else {
          // Erro de rede - manter o token e o usuário atual
          console.warn('Erro de rede ao verificar auth, mantendo sessão:', error.message);
          if (user) {
            setLoading(false);
            isCheckingAuth.current = false;
            return;
          }
        }
      }
    }
    
    // Tentar autenticação via cookie de sessão (Google Auth)
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      if (response.data && response.data.id) {
        setUser(response.data);
        setLoading(false);
        isCheckingAuth.current = false;
        return;
      }
    } catch (error) {
      console.log('Session auth check: not logged in');
    }
    
    setUser(null);
    setLoading(false);
    isCheckingAuth.current = false;
  };

  useEffect(() => {
    checkAuth();
    
    // Interceptor global para 401 - logout apenas quando o servidor confirma token inválido
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          const url = error.config?.url || '';
          // Não fazer logout se o erro for na própria chamada de auth/me (já tratado acima)
          if (!url.includes('/auth/me') && !url.includes('/auth/login') && !url.includes('/auth/register')) {
            const token = localStorage.getItem('token');
            if (token) {
              console.warn('401 em chamada autenticada, removendo token');
              localStorage.removeItem('token');
              setUser(null);
            }
          }
        }
        return Promise.reject(error);
      }
    );
    
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const login = async (email, password, rememberMe = false) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      
      localStorage.setItem('token', response.data.token);
      
      if (rememberMe) {
        localStorage.setItem('rememberMe', 'true');
        localStorage.setItem('tokenExpiry', String(Date.now() + 30 * 24 * 60 * 60 * 1000));
      } else {
        localStorage.setItem('rememberMe', 'false');
        localStorage.setItem('tokenExpiry', String(Date.now() + 24 * 60 * 60 * 1000));
      }
      
      setUser(response.data.user);
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const register = async (email, name, password, role) => {
    try {
      const response = await axios.post(`${API}/auth/register`, { email, name, password, role });
      localStorage.setItem('token', response.data.token);
      setUser(response.data.user);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Falha no cadastro' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('rememberMe');
    localStorage.removeItem('tokenExpiry');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, loading, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
